import { motion } from 'framer-motion';
import { useVault } from '@/lib/supabase/VaultContext';
import type { Item, ItemType } from '@/lib/supabase/types';
import { spring } from '@/lib/motion/presets';
import {
  KeyRound,
  FileText,
  FileUp,
  CreditCard,
  UserSquare,
  Binary,
  Star,
  AlertTriangle,
  FileImage,
} from 'lucide-react';

interface ItemCardProps {
  item: Item;
  onClick: () => void;
}

export function ItemCard({ item, onClick }: ItemCardProps) {
  const { toggleFavorite } = useVault();

  const typeIcons: Record<ItemType, React.ReactNode> = {
    file: <FileUp className="size-4" />,
    photo: <FileImage className="size-4" />,
    note: <FileText className="size-4" />,
    password: <KeyRound className="size-4" />,
    card: <CreditCard className="size-4" />,
    id: <UserSquare className="size-4" />,
    software_key: <Binary className="size-4" />,
  };

  // Extract type-specific subtitle or secondary details
  function renderSecondaryDetails() {
    const { payload } = item;
    if (!payload) return null;

    switch (item.item_type) {
      case 'file':
      case 'photo': {
        const meta = item.storage_meta;
        if (!meta) return <span className="text-2xs text-ink-faint italic">No file attached</span>;
        const sizeStr = meta.size ? `${(meta.size / 1024 / 1024).toFixed(2)} MB` : '';
        const dimStr = meta.width && meta.height ? `${meta.width}×${meta.height}` : '';
        const ext = meta.mime?.split('/')[1]?.toUpperCase() || '';
        return (
          <div className="flex flex-wrap items-center gap-1.5 mt-0.5">
            {ext && (
              <span className="text-3xs px-1 py-0.25 bg-bg/50 rounded border border-line text-ink-faint font-mono uppercase">
                {ext}
              </span>
            )}
            {sizeStr && <span className="text-2xs text-ink-muted font-mono">{sizeStr}</span>}
            {dimStr && <span className="text-3xs text-ink-faint font-mono">{dimStr}</span>}
          </div>
        );
      }
      case 'password':
        return payload.username ? (
          <span className="truncate block font-mono text-2xs text-ink-muted">{payload.username}</span>
        ) : null;
      case 'card':
        return payload.cardNumber ? (
          <span className="truncate block font-mono text-2xs text-ink-muted">
            •••• {payload.cardNumber.slice(-4)}
          </span>
        ) : null;
      case 'software_key':
        return payload.email ? (
          <span className="truncate block font-mono text-2xs text-ink-muted">{payload.email}</span>
        ) : null;
      case 'id':
        return (
          <div className="flex flex-wrap items-center gap-1.5 mt-0.5">
            {payload.type && (
              <span className="text-2xs text-ink-muted truncate max-w-[100px]">{payload.type}</span>
            )}
            {payload.country && (
              <span className="text-3xs px-1 py-0.25 bg-bg/50 rounded border border-line text-ink-faint uppercase">
                {payload.country.slice(0, 3)}
              </span>
            )}
          </div>
        );
      case 'note': {
        // Strip HTML tags for preview text
        const plainText = payload.content
          ? payload.content.replace(/<[^>]*>/g, '').trim()
          : '';
        return plainText ? (
          <span className="truncate block text-2xs text-ink-muted italic">
            &ldquo;{plainText}&rdquo;
          </span>
        ) : (
          <span className="text-2xs text-ink-faint italic">Empty note</span>
        );
      }
      default:
        return null;
    }
  }

  // Check ID expiration alert for card borders/badges
  const isExpiringSoon = () => {
    if (item.item_type !== 'id' || !item.payload?.expiryDate) return false;
    const expiry = new Date(item.payload.expiryDate);
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const diffTime = expiry.getTime() - today.getTime();
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    return diffDays <= 30; // Expired or expiring within 30 days
  };

  return (
    <motion.div
      layout
      whileHover={{ y: -3 }}
      whileTap={{ scale: 0.98 }}
      transition={spring}
      onClick={onClick}
      className={`group relative cursor-pointer overflow-hidden rounded-md border p-4 text-left transition-all hover:shadow-cardHover bg-surface ${
        isExpiringSoon() ? 'border-danger/30 hover:border-danger/60' : 'border-line hover:border-line-strong'
      }`}
    >
      {/* Expiry glow/warning strip */}
      {isExpiringSoon() && (
        <div className="absolute top-0 inset-x-0 h-[2px] bg-danger" />
      )}

      <div className="flex items-start justify-between gap-3">
        {/* Type Icon */}
        <span className="grid size-8 place-items-center rounded border border-line bg-bg text-ink-muted group-hover:text-accent transition-colors">
          {typeIcons[item.item_type]}
        </span>

        {/* Action Buttons */}
        <div className="flex items-center gap-1.5 opacity-0 group-hover:opacity-100 transition-opacity">
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              toggleFavorite(item.id);
            }}
            className={`rounded p-1 transition-colors ${
              item.favorite ? 'text-accent hover:text-accent-soft' : 'text-ink-faint hover:text-ink-muted'
            }`}
            title={item.favorite ? 'Unfavorite' : 'Favorite'}
          >
            <Star className={`size-3.5 ${item.favorite ? 'fill-current' : ''}`} />
          </button>
        </div>
      </div>

      {/* Title & Details */}
      <div className="mt-3.5 space-y-1">
        <h4 className="text-xs font-semibold tracking-tight text-ink group-hover:text-accent transition-colors truncate">
          {item.title}
        </h4>
        {renderSecondaryDetails()}
      </div>

      {/* Tags & Favorite status */}
      <div className="mt-4 flex items-center justify-between gap-2 border-t border-line/40 pt-2.5">
        {/* Tags */}
        <div className="flex flex-wrap gap-1 max-w-[80%] overflow-hidden max-h-5">
          {item.tags && item.tags.length > 0 ? (
            item.tags.slice(0, 2).map((tag) => (
              <span
                key={tag.id}
                className="rounded bg-bg px-1.5 py-0.5 text-4xs font-mono tracking-tight text-ink-faint uppercase"
              >
                {tag.name}
              </span>
            ))
          ) : (
            <span className="text-4xs text-ink-faint font-mono uppercase tracking-tight">No tags</span>
          )}
          {item.tags && item.tags.length > 2 && (
            <span className="rounded bg-bg px-1 py-0.5 text-4xs text-ink-faint font-mono">
              +{item.tags.length - 2}
            </span>
          )}
        </div>

        {/* Favorite marker in default view (if favorited) */}
        {item.favorite && (
          <Star className="size-3 text-accent fill-current group-hover:hidden transition-all" />
        )}

        {/* ID Expiry alert symbol */}
        {isExpiringSoon() && (
          <AlertTriangle className="size-3 text-danger fill-none animate-pulse" />
        )}
      </div>
    </motion.div>
  );
}
